import type { ChartDataset, ChartType, Plugin } from "chart.js";

export type LabeledChartDataset<TType extends ChartType> = ChartDataset<TType, number[]> & {
  valueLabels?: string[];
};

export function withValueLabels<TType extends ChartType>(
  dataset: ChartDataset<TType, number[]>,
  valueLabels: string[]
): LabeledChartDataset<TType> {
  return { ...dataset, valueLabels } as unknown as LabeledChartDataset<TType>;
}

export const chartValueLabelsPlugin: Plugin = {
  id: "chartValueLabels",
  afterDatasetsDraw(chart) {
    const { chartArea, ctx } = chart;
    ctx.save();
    ctx.font = '700 11px "Segoe UI", Arial, sans-serif';
    ctx.textBaseline = "middle";

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const labels = (dataset as LabeledChartDataset<ChartType>).valueLabels;
      if (!labels?.length) return;

      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        const label = labels[index];
        if (!label) return;

        const point = element.getProps(["x", "y", "base"], true) as unknown as {
          x: number;
          y: number;
          base?: number;
        };
        const textWidth = ctx.measureText(label).width;
        const horizontalBar = meta.type === "bar" && chart.options.indexAxis === "y";
        const verticalBar = meta.type === "bar" && !horizontalBar;
        const linePoint = meta.type === "line";
        let x = point.x;
        let y = point.y;
        let align: CanvasTextAlign = "center";
        let color = "#334155";

        if (horizontalBar) {
          const base = point.base ?? chartArea.left;
          const barWidth = Math.abs(point.x - base);
          const fitsInside = barWidth >= textWidth + 20;
          x = fitsInside ? point.x - 8 : point.x + 8;
          align = fitsInside ? "right" : "left";
          color = fitsInside ? "#ffffff" : "#334155";

          if (!fitsInside && x + textWidth > chartArea.right) {
            x = point.x - 8;
            align = "right";
            color = "#ffffff";
          }
        } else if (verticalBar) {
          const base = point.base ?? chartArea.bottom;
          const barHeight = Math.abs(base - point.y);
          const fitsInside = barHeight >= 24;
          y = fitsInside ? point.y + 11 : Math.max(chartArea.top + 9, point.y - 11);
          color = fitsInside ? "#ffffff" : "#334155";
          x = Math.max(chartArea.left + textWidth / 2, Math.min(chartArea.right - textWidth / 2, point.x));
        } else {
          y = Math.max(chartArea.top + 9, point.y - 20);
          color = typeof dataset.borderColor === "string" ? dataset.borderColor : "#334155";
          x = Math.max(chartArea.left + textWidth / 2, Math.min(chartArea.right - textWidth / 2, point.x));
        }

        ctx.textAlign = align;
        ctx.fillStyle = color;
        if (linePoint) {
          ctx.lineWidth = 3;
          ctx.strokeStyle = "rgba(255, 255, 255, 0.96)";
          ctx.strokeText(label, x, y);
        }
        ctx.fillText(label, x, y);
      });
    });

    ctx.restore();
  }
};
